import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authApi } from '@/api/auth'
import type { LoginRequest, TeacherRegisterRequest, User, UserRole } from '@/types/api'

const TOKEN_KEY = 'material_token'
const REFRESH_TOKEN_KEY = 'material_refresh_token'
const USER_KEY = 'material_user'

function parseJwtPayload(token: string): { sub?: string; role?: UserRole; exp?: number; typ?: string } {
  try {
    const base64 = token.split('.')[1]
    const json = atob(base64.replace(/-/g, '+').replace(/_/g, '/'))
    return JSON.parse(json)
  } catch {
    return {}
  }
}

export const useAuthStore = defineStore('auth', () => {
  // State
  const token = ref<string | null>(localStorage.getItem(TOKEN_KEY))
  const user = ref<User | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)

  // Getters
  const isLoggedIn = computed(() => !!token.value)
  const userRole = computed<UserRole | null>(() => {
    if (!token.value) return null
    return parseJwtPayload(token.value).role || null
  })
  const userId = computed<number | null>(() => {
    if (!token.value) return null
    const sub = parseJwtPayload(token.value).sub
    return sub ? Number(sub) : null
  })
  const isTeacher = computed(() => userRole.value === 'teacher')
  const isAdmin = computed(() => userRole.value === 'admin' || userRole.value === 'super_admin')
  const isSuperAdmin = computed(() => userRole.value === 'super_admin')
  // 仓管（admin 角色，但非 super_admin）
  const isWarehouseAdmin = computed(() => userRole.value === 'admin')
  const warehouseId = computed(() => user.value?.warehouse_id ?? null)
  const isApproved = computed(() => user.value?.is_approved ?? true)
  const mustChangePassword = computed(() => !!user.value?.must_change_password)

  // Actions
  async function login(credentials: LoginRequest) {
    loading.value = true
    error.value = null
    try {
      const response = await authApi.login(credentials)
      token.value = response.data.access_token
      localStorage.setItem(TOKEN_KEY, response.data.access_token)
      if (response.data.refresh_token) {
        localStorage.setItem(REFRESH_TOKEN_KEY, response.data.refresh_token)
      }
      await fetchMe()
      return true
    } catch (err: any) {
      error.value = err.response?.data?.detail || '登录失败，请检查用户名和密码'
      return false
    } finally {
      loading.value = false
    }
  }

  async function register(data: TeacherRegisterRequest) {
    loading.value = true
    error.value = null
    try {
      const response = await authApi.register(data)
      return response.data
    } catch (err: any) {
      error.value = err.response?.data?.detail || '注册失败'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function fetchMe() {
    if (!token.value) return
    try {
      const res = await authApi.me()
      user.value = res.data
      localStorage.setItem(USER_KEY, JSON.stringify(res.data))
    } catch {
      user.value = null
      localStorage.removeItem(USER_KEY)
    }
  }

  function logout() {
    token.value = null
    user.value = null
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(REFRESH_TOKEN_KEY)
    localStorage.removeItem(USER_KEY)
  }

  async function init() {
    const saved = localStorage.getItem(TOKEN_KEY)
    if (saved) {
      token.value = saved
      const savedUser = localStorage.getItem(USER_KEY)
      if (savedUser) {
        try {
          user.value = JSON.parse(savedUser)
        } catch {
          user.value = null
        }
      }
      // 启动时刷新一次 me（拿到最新的 is_approved / must_change_password）
      try {
        await fetchMe()
      } catch {
        // 401 由 axios interceptor 处理；这里忽略
      }
    }
  }

  return {
    token,
    user,
    loading,
    error,
    isLoggedIn,
    userRole,
    userId,
    isTeacher,
    isAdmin,
    isSuperAdmin,
    isWarehouseAdmin,
    warehouseId,
    isApproved,
    mustChangePassword,
    login,
    register,
    fetchMe,
    logout,
    init,
  }
})
